import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {boolean} */
  #initialUrlHadVariant = false;

  connectedCallback() {
    this.#initialUrlHadVariant = !!new URLSearchParams(window.location.search).get('variant');
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    document.addEventListener('variant:url-sync', this.#handleVariantUrlSync, { signal });
    this.#scheduleVariantMediaSync();
  }

  #handleVariantUrlSync = (event) => {
    const mediaId = event.detail?.mediaId;
    const variantId = event.detail?.variantId;

    if (this.dataset.skipVariantMediaOnLoad === 'true') {
      if (mediaId) {
        this.removeAttribute('data-skip-variant-media-on-load');
        this.#syncVariantMediaById(String(mediaId));
        return;
      }
      if (!this.#initialUrlHadVariant) return;
    }

    if (mediaId) {
      this.#syncVariantMediaById(String(mediaId));
      return;
    }

    if (variantId) {
      this.#syncVariantMediaFromUrl(variantId);
    }
  };

  /**
   * Schedules media sync on load and after slideshow initialization.
   */
  #scheduleVariantMediaSync() {
    if (!this.#initialUrlHadVariant) return;

    const sync = () => this.#syncVariantMediaFromUrl();
    sync();
    requestAnimationFrame(sync);
    setTimeout(sync, 50);
    setTimeout(sync, 200);
    setTimeout(sync, 500);
    setTimeout(sync, 1000);
    setTimeout(sync, 1500);
  }

  /**
   * @returns {Record<string, string> | null}
   */
  #getVariantMediaMap() {
    const mapScript = this.querySelector('script[data-variant-media-map]');
    if (!mapScript?.textContent) return null;

    try {
      return JSON.parse(mapScript.textContent);
    } catch {
      return null;
    }
  }

  /**
   * Selects a slide by media id directly.
   *
   * @param {string} mediaId
   */
  #syncVariantMediaById(mediaId) {
    const applyVariantMedia = (attempt = 0) => {
      if (!this.slideshow?.refs?.slides?.length || !this.slideshow.isReady) {
        if (attempt < 120) requestAnimationFrame(() => applyVariantMedia(attempt + 1));
        return;
      }

      const slides = this.slideshow.refs.slides;
      const slideExists = slides.some((slide) => slide.getAttribute('slide-id') == mediaId);
      if (!slideExists) {
        if (attempt < 120) requestAnimationFrame(() => applyVariantMedia(attempt + 1));
        return;
      }

      this.dataset.initialVariantMediaId = mediaId;
      this.#selectVariantMedia(mediaId);
    };

    requestAnimationFrame(() => applyVariantMedia());
  }

  /**
   * On page load, select the variant media when ?variant= is in the URL.
   *
   * @param {string} [variantIdOverride]
   */
  #syncVariantMediaFromUrl(variantIdOverride) {
    const variantId = variantIdOverride || new URLSearchParams(window.location.search).get('variant');
    if (!variantId) return;

    if (
      this.dataset.skipVariantMediaOnLoad === 'true' &&
      !this.#initialUrlHadVariant &&
      !variantIdOverride
    ) {
      return;
    }

    const applyVariantMedia = (attempt = 0) => {
      if (!this.slideshow?.refs?.slides?.length || !this.slideshow.isReady) {
        if (attempt < 120) requestAnimationFrame(() => applyVariantMedia(attempt + 1));
        return;
      }

      const featuredMediaId = this.#getFeaturedMediaIdForVariant(variantId);
      if (!featuredMediaId) return;

      const slides = this.slideshow.refs.slides;
      const slideExists = slides.some((slide) => slide.getAttribute('slide-id') == featuredMediaId);
      if (!slideExists) {
        if (attempt < 120) requestAnimationFrame(() => applyVariantMedia(attempt + 1));
        return;
      }

      this.dataset.initialVariantMediaId = String(featuredMediaId);
      this.#selectVariantMedia(String(featuredMediaId));
    };

    applyVariantMedia();
  }

  /**
   * @param {string | null | undefined} variantId
   * @returns {string | number | undefined}
   */
  #getFeaturedMediaIdForVariant(variantId) {
    if (variantId) {
      const urlVariant = new URLSearchParams(window.location.search).get('variant');
      if (
        urlVariant &&
        String(variantId) === String(urlVariant) &&
        this.dataset.initialVariantMediaId
      ) {
        return this.dataset.initialVariantMediaId;
      }

      const map = this.#getVariantMediaMap();
      const mapMediaId = map?.[variantId] ?? map?.[String(variantId)];
      if (mapMediaId != null && mapMediaId !== '') return mapMediaId;

      const section = this.closest('.shopify-section');
      const optionInput = section?.querySelector(`variant-picker input[data-variant-id="${variantId}"]`);
      const optionMediaId = optionInput?.dataset.optionMediaId;
      if (optionMediaId) return optionMediaId;

      const variantJson = section?.querySelector('variant-picker script[type="application/json"]')?.textContent;
      if (variantJson) {
        try {
          const variant = JSON.parse(variantJson);
          if (variant?.featured_media?.id && String(variant.id) === String(variantId)) {
            return variant.featured_media.id;
          }
        } catch {
          // fall through
        }
      }
    }

    if (this.dataset.initialVariantMediaId) {
      return this.dataset.initialVariantMediaId;
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by selecting the variant media or morphing grid content.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    if (event.detail.data.newProduct) {
      this.replaceWith(newMediaGallery);
      return;
    }

    const featuredMediaId = event.detail.resource?.featured_media?.id;

    if (this.presentation === 'carousel') {
      if (featuredMediaId) {
        this.dataset.initialVariantMediaId = String(featuredMediaId);
        this.#selectVariantMedia(String(featuredMediaId));
      }
      return;
    }

    morph(this, newMediaGallery, { childrenOnly: true });
    this.updatedCallback();

    if (featuredMediaId) {
      requestAnimationFrame(() => {
        this.#selectVariantMedia(String(featuredMediaId));
      });
    }
  };

  /**
   * Selects the slide for the variant media and syncs thumbnail state.
   *
   * @param {string} mediaId - The featured media id of the selected variant.
   */
  #selectVariantMedia(mediaId) {
    const trySelect = (attempt = 0) => {
      const slideshow = this.slideshow;
      if (!slideshow?.refs?.slides?.length || !slideshow.isReady) {
        if (attempt < 120) requestAnimationFrame(() => trySelect(attempt + 1));
        return;
      }

      const slides = slideshow.refs.slides;
      let index = slides.findIndex((slide) => slide.getAttribute('slide-id') == mediaId);
      let resolvedMediaId = mediaId;

      if (index < 0 && this.dataset.initialSlideIndex) {
        const parsed = parseInt(this.dataset.initialSlideIndex, 10);
        if (!isNaN(parsed) && parsed >= 0 && parsed < slides.length) {
          index = parsed;
          resolvedMediaId = slides[index].getAttribute('slide-id') || mediaId;
        }
      }

      if (index < 0) return;

      slideshow.select(index, undefined, { animate: false, force: true });
      this.#updateThumbnailCurrent(resolvedMediaId);
    };

    trySelect();
  }

  /**
   * Updates which thumbnail is marked as current without reordering thumbnails.
   *
   * @param {string} mediaId - The featured media id of the selected variant.
   */
  #updateThumbnailCurrent(mediaId) {
    const slides = this.slideshow?.refs.slides ?? [];
    const slideIndex = slides.findIndex((slide) => slide.getAttribute('slide-id') === mediaId);

    if (slideIndex < 0) return;

    const thumbnails = this.querySelectorAll('.slideshow-controls__thumbnail');

    thumbnails.forEach((thumbnail, index) => {
      if (index === slideIndex) {
        thumbnail.setAttribute('aria-current', 'true');
      } else {
        thumbnail.removeAttribute('aria-current');
      }
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
